import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import ButtonWithSpinner from "../ButtonWithSpinner";
import YesOrNoDialog from "../YesOrNoDialog";
import { useApi } from "../../utils/Api";
import Translate from "../../utils/Translate";
import Preloader from "../Preloader";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { groupBy } from "lodash";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";

const _templatesTabId = "templatesTabId";

function ExternalSkillListItem({ code, name, description, isSelected, disabled, onToggle, groupId }) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        disabled={disabled}
        className={"selectListButton" + (isSelected ? " selected" : "")}
        onClick={() => onToggle(code, groupId)}
      >
        <ListItemText primary={name} secondary={description} />
      </ListItemButton>
    </ListItem>
  );
}

function ExternalSkillGroupsTemplatesTab({ externalSkillGroupTemplates, groupIdWithSelectedSkills, handleToggle }) {
  if (externalSkillGroupTemplates.filter((x) => !x.ssgSkillTypeId).length > 1) {
    console.log("externalSkillGroupTemplates", externalSkillGroupTemplates);
    throw new Error("There should only be one external skill group template without skilltypeid");
  }

  const [selectedTemplateGroupId, setSelectedTemplateGroupId] = useState(null);

  useEffect(() => {
    if (!selectedTemplateGroupId && (externalSkillGroupTemplates?.length ?? 0) > 0) {
      setSelectedTemplateGroupId(externalSkillGroupTemplates[0].groupId);
    }
  }, [externalSkillGroupTemplates, selectedTemplateGroupId]);

  if (!externalSkillGroupTemplates || externalSkillGroupTemplates.length === 0) {
    return null;
  }

  const selectedTemplate = !!selectedTemplateGroupId
    ? externalSkillGroupTemplates.find((x) => x.groupId == selectedTemplateGroupId)
    : null;

  return (
    <TabPanel value={_templatesTabId}>
      <FormControl fullWidth sx={{ mb: 3.0 }}>
        <FormLabel id="template-select-label" sx={{ mb: 0.5 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            {Translate.get("ExternalSkillGroupTemplate")}
            <Tooltip
              title={<div style={{ whiteSpace: "pre-line" }}>{Translate.get("ExternalSkillGroupTemplateInfo")}</div>}
            >
              <InfoIcon color="primary" />
            </Tooltip>
          </Stack>
        </FormLabel>
        <Select
          fullWidth
          labelId="template-select-label"
          id="template-select"
          disabled={false}
          value={selectedTemplateGroupId ?? ""}
          onChange={(ev) => setSelectedTemplateGroupId(ev.target.value)}
        >
          {externalSkillGroupTemplates.map((template) => (
            <MenuItem value={template.groupId} key={template.groupId}>
              {!!template.ssgSkillTypeId ? (
                template.groupName
              ) : (
                <Typography
                  noWrap
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  {Translate.get("ExternalSkillGroupNoTemplate")}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormLabel sx={{ mb: 0.5 }}>{Translate.get("ExternalSkillGroupTemplateSkills")}</FormLabel>
      <List>
        {selectedTemplate?.externalSkills.map((externalSkill) => (
          <ExternalSkillListItem
            key={externalSkill.code}
            {...externalSkill}
            groupId={selectedTemplateGroupId}
            disabled={!!groupIdWithSelectedSkills && groupIdWithSelectedSkills != selectedTemplateGroupId}
            onToggle={handleToggle}
          />
        ))}
      </List>
    </TabPanel>
  );
}

export default function SkillnationExternalSkillsDialog({ courseId, onClose }) {
  const [externalSkillGroups, setExternalSkillGroups] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [selectedTabValue, setSelectedTabValue] = useState("");
  const theme = useTheme();
  const isSmallScreenOrGreater = useMediaQuery(theme.breakpoints.up("sm"));
  const api = useApi();

  useEffect(() => {
    api
      .fetch(
        `${import.meta.env.VITE_MAIN_URL}integrations/SsgSkillnation/courses/${courseId}/external-skill-groups/all`,
        false,
        "GET",
      )
      .then((data) => {
        if (data.isSuccessful) {
          const items = data.externalSkillGroups;
          setExternalSkillGroups(items);

          const selectedIndex = items.findIndex((grp) => grp.externalSkills.some((es) => !!es.isSelected));
          setSelectedTabValue(selectedIndex > -1 ? items[selectedIndex].groupId : items[0].groupId);
        }
        setIsDataFetched(true);
      });
  }, [courseId, api]);

  async function uploadUpdatedSkillSelection() {
    const selectedExternalSkillGroups = externalSkillGroups.filter((grp) =>
      grp.externalSkills.some((es) => !!es.isSelected),
    );
    if (selectedExternalSkillGroups.length > 1) {
      throw new Error("Skills from more than one ExternalSkillGroup have been selected");
    }

    const request = {
      ...selectedExternalSkillGroups[0],
      externalSkills: selectedExternalSkillGroups[0].externalSkills.filter((es) => !!es.isSelected),
    };
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}integrations/SsgSkillnation/courses/${courseId}/external-skill-groups`,
      request,
      "PUT",
    );
    if (response.isSuccessful === true) {
      onClose(); // Close dialog after saving
    }
  }

  function handleToggle(code, groupId) {
    const groupIndex = externalSkillGroups.findIndex((grp) => grp.groupId === groupId);
    if (groupIndex < 0) {
      return;
    }

    const externalSkillIndex = externalSkillGroups[groupIndex].externalSkills.findIndex((t) => t.code === code);
    if (externalSkillIndex < 0) {
      return;
    }

    const groupsCopy = [...externalSkillGroups];
    const groupCopy = { ...externalSkillGroups[groupIndex] };
    const externalSkillsCopy = [...groupCopy.externalSkills];
    const externalSkillCopy = { ...externalSkillsCopy[externalSkillIndex] };
    externalSkillCopy.isSelected = !externalSkillCopy.isSelected;
    externalSkillsCopy.splice(externalSkillIndex, 1, externalSkillCopy);
    groupCopy.externalSkills = externalSkillsCopy;
    groupsCopy.splice(groupIndex, 1, groupCopy);
    setExternalSkillGroups(groupsCopy);
  }

  const groupIdWithSelectedSkills = externalSkillGroups.find((grp) =>
    grp.externalSkills.some((es) => !!es.isSelected),
  )?.groupId;

  return (
    <>
      {!isDataFetched && <Preloader zIndexOverride={5000} />}
      {isDataFetched && (
        <Dialog open={true} onClose={onClose} className="mediumCourseDialog">
          <DialogTitle>{`${Translate.get("SsgSkillnationName")} ${Translate.get("EditCourseSkills")}`}</DialogTitle>
          <DialogContent>
            <Box>
              <Typography sx={{ margin: "0 0 24px" }}>{Translate.get("SkillnationEditCourseSkillsInfo")}</Typography>
              <TabContext value={selectedTabValue}>
                <Box>
                  <TabList onChange={(event, value) => setSelectedTabValue(value)} centered>
                    {externalSkillGroups
                      .filter((grp) => !grp.isTemplate)
                      .map((grp) => (
                        <Tab label={grp.groupName} value={grp.groupId} />
                      ))}
                    <Tab
                      label={Translate.get("CreateNewExternalSkillGroup")}
                      value={_templatesTabId}
                      icon={<AddIcon sx={{ mb: 0.2 }} />}
                      iconPosition="end"
                    />
                  </TabList>
                </Box>
                {externalSkillGroups
                  .filter((grp) => !grp.isTemplate)
                  .map((grp) => {
                    return (
                      <TabPanel key={grp.groupId} value={grp.groupId}>
                        <List>
                          {grp.externalSkills.map((externalSkill) => (
                            <ExternalSkillListItem
                              key={externalSkill.code}
                              {...externalSkill}
                              groupId={grp.groupId}
                              disabled={!!groupIdWithSelectedSkills && groupIdWithSelectedSkills != grp.groupId}
                              onToggle={handleToggle}
                            />
                          ))}
                        </List>
                      </TabPanel>
                    );
                  })}
                <ExternalSkillGroupsTemplatesTab
                  externalSkillGroupTemplates={externalSkillGroups.filter((grp) => !!grp.isTemplate)}
                  groupIdWithSelectedSkills={groupIdWithSelectedSkills}
                  handleToggle={handleToggle}
                />
              </TabContext>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{Translate.get("Cancel")}</Button>
            <ButtonWithSpinner onClick={uploadUpdatedSkillSelection}>{Translate.get("Save")}</ButtonWithSpinner>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
